import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { useProfileState } from '@/contexts/ProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import { analysis as analysisApi } from '@/lib/api';
import { CoverLetterModal } from '@/pages/Dashboard/RoleExplorer';
import { downloadCoverLetterPdf, emailCoverLetterAsPdf } from '@/lib/coverLetterPdf';
import { trackEvent, trackDeeplink, EVENTS } from '@/lib/analytics';

/* ================================================================================
   MY PROGRESS — two-section rebuild
   -----------------------------------------------------------------------------
   Section 1 — "Quick Wins" (auto-tracked, in-app)
     Exactly 3 tasks. Dashed checkboxes (system-controlled, NOT user-tickable).
     Auto-complete when the user performs the action in the app:
       - jd_ran          → runs a JD analysis
       - role_expanded   → expands a role card in Role Explorer
       - resource_opened → opens an event/resource in Network In
     Sticky per-user forever (persisted in progress.quick_wins). Confetti fires
     on the transition from unchecked → checked.

   Section 2 — "Build Toward It" (manual, real-world)
     User-checkable (solid checkbox, confetti on check). Sourced from:
       - analysis.call2.build_toward_it   (LLM-generated, gap-derived, tagged
                                            skill/network/positioning, with steps
                                            + optional resource_url + tool_tip)
       - progress.saved_jd_actions        (tasks added from the JD Analyzer —
                                            preserved from the old design;
                                            they render inside this section)

   Legacy `milestones_critical/quick/build` are NOT rendered here anymore
   (per user instruction: "remove any old generic tasks and the old flat
   structure entirely"). They still exist on the analysis payload for
   back-compat with other views (Overview, PrintReport).

   The progress ring at top counts ALL tasks across both sections.
   ================================================================================ */

/* ---------------------------------------------------------------------
   Quick Wins: static definition of the three auto-tracked achievements.
   Each maps to one core tool + a deep-link into that specific section.
   --------------------------------------------------------------------- */
const QUICK_WINS = [
  {
    key: 'jd_ran',
    title: 'Benchmark yourself against a real job posting',
    tag: 'JD Analyzer',
    done_label: 'Done · JD Analyzer',
    how_it_completes: 'Ticked automatically when the JD Analyzer finishes scoring a posting against your CV.',
    steps: [
      'Open the JD Analyzer.',
      "Paste a job description you'd actually apply for.",
      'Click Analyze and wait for the score + rewrites.',
    ],
    cta_label: 'Open JD Analyzer',
    cta_path: '/dashboard/jd',
  },
  {
    key: 'role_expanded',
    title: 'Explore your role paths',
    tag: 'Role Explorer',
    done_label: 'Done · Role Explorer',
    how_it_completes: 'Ticked automatically once you expand at least one role card in Role Explorer.',
    steps: [
      'Open Role Explorer.',
      'Pick a role that catches your eye.',
      'Click Show details to see the fit rationale + skill gaps.',
    ],
    cta_label: 'Open Role Explorer',
    cta_path: '/dashboard/roles',
  },
  {
    key: 'resource_opened',
    title: 'Explore one event or resource in your growth area',
    tag: 'Events & Resources',
    done_label: 'Done · Events & Resources',
    how_it_completes: 'Ticked automatically once you open at least one event, community or resource from the Events & Resources grid.',
    steps: [
      'Open Network In.',
      'Scroll to Events & Resources.',
      'Open the resource that maps to your biggest gap.',
    ],
    cta_label: 'Open Events & Resources',
    cta_path: '/dashboard/network#events-resources',
  },
];

/* ---------------------------------------------------------------------
   Small helpers
   --------------------------------------------------------------------- */
function fireConfetti(originX, originY) {
  const x = originX / window.innerWidth;
  const y = originY / window.innerHeight;
  confetti({
    particleCount: 55,
    spread: 65,
    startVelocity: 32,
    ticks: 90,
    origin: { x, y },
    colors: ['#4CD964', '#3AA856', '#0A0A0A', '#1F4D2A'],
    scalar: 0.9,
  });
}

/* Strip any leading numbering the LLM sometimes prepends to a step string
   ("1.", "2)", "• "). The UI's <ol> already renders numbers, so a raw step
   of "1. Do X" would render as "1. 1. Do X". This is a display-side safety
   net for analyses saved before the validator started scrubbing. */
function stripLeadingNumbering(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^\s*(?:\d{1,2}[.)\-:\s]+|[-•*]\s+)/, '').trim();
}

/* Bold the "deliverable phrase" in the LAST step of a Build-Toward-It task.
   The LLM's step often reads like:
     "In your next review, present the budget to your CFO — that presentation
      becomes the board-level exposure evidence for Milestone 2."
   The deliverable is the "becomes …" clause. We highlight it because that
   phrase is exactly what the user should paste as a CV / LinkedIn bullet.

   Heuristic (best-effort, never throws):
     1. If the sentence contains " — " or " – " → bold everything after the dash.
     2. Else if it contains " becomes " / " which becomes " → bold from there.
     3. Else if it contains "for Milestone" / "evidence for" → bold from there.
     4. Otherwise → bold the last clause after the last comma (if the tail is
        at least 6 words long — avoids highlighting trivial trailing phrases).
     5. Final fallback: render plain text (no highlight). */
function BoldDeliverable({ text }) {
  const raw = typeof text === 'string' ? text : '';
  if (!raw) return null;
  const patterns = [
    / [—–] /,                                // em-/en-dash
    / \bbecomes\b /i,
    / \bwhich becomes\b /i,
    / \bthat becomes\b /i,
    / \bthat (?:presentation|artifact|deck|note|memo|report|writeup|write-up) becomes\b /i,
    / \bevidence for\b /i,
    / \bfor Milestone\b /i,
  ];
  for (const re of patterns) {
    const m = raw.match(re);
    if (m && typeof m.index === 'number') {
      const head = raw.slice(0, m.index).trim();
      const tail = raw.slice(m.index).replace(/^[\s—–]+/, '').trim();
      if (tail && tail.split(/\s+/).length >= 3) {
        return (
          <>
            {head}
            {head ? ' ' : ''}
            <strong className="font-semibold text-sd-black">{tail}</strong>
          </>
        );
      }
    }
  }
  // Fallback: bold clause after the last comma when the tail is substantial.
  const lastComma = raw.lastIndexOf(',');
  if (lastComma > 0) {
    const tail = raw.slice(lastComma + 1).trim();
    if (tail.split(/\s+/).length >= 6) {
      return (
        <>
          {raw.slice(0, lastComma + 1)}{' '}
          <strong className="font-semibold text-sd-black">{tail}</strong>
        </>
      );
    }
  }
  return <>{raw}</>;
}

/* Deep-link button used across My Progress. Fires a `deeplink_clicked` event
   and navigates via React Router so we never full-reload back to /dashboard. */
function DeepLinkButton({ to, label, testId, small = false }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        trackDeeplink(to);
        navigate(to);
      }}
      className={
        (small
          ? 'sd-btn-primary inline-flex items-center gap-2 !py-1.5 !px-3 !text-[12px]'
          : 'sd-btn-primary inline-flex items-center gap-2 !py-2 !px-3.5 !text-[12.5px]')
      }
      data-testid={testId}
    >
      {label}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

/* ================================================================================
   TOP-LEVEL PAGE
   ================================================================================ */
export default function MyProgress() {
  const { analysis, progress, toggleJdAction, toggleJdActionCompletion } = useProfileState();
  const c2 = analysis?.call2 || {};

  // ------- Quick Wins -------
  const qwDone = new Set(progress?.quick_wins || []);
  const qwTotal = QUICK_WINS.length;
  const qwCompleted = QUICK_WINS.filter((q) => qwDone.has(q.key)).length;

  // ------- Build Toward It (LLM + JD-added) -------
  const buildTowardIt = Array.isArray(c2.build_toward_it) ? c2.build_toward_it : [];
  const savedJd = progress?.saved_jd_actions || [];
  const jdDoneSet = new Set(progress?.completed_jd_actions || []);
  const btiCount = buildTowardIt.length + savedJd.length;
  const btiDone =
    // LLM tasks — completion is tracked in completed_milestones with `bti_` prefix
    (progress?.completed_milestones || []).filter((k) => k.startsWith('bti_')).length +
    savedJd.filter((k) => jdDoneSet.has(k)).length;

  // ------- Combined progress ring -------
  const total = qwTotal + btiCount;
  const completed = qwCompleted + btiDone;
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);

  // ------- Biggest lever subtitle -------
  const biggestLever = useMemo(() => deriveBiggestLever(analysis), [analysis]);

  return (
    <div>
      <h1 className="text-[30px] font-semibold text-sd-black mb-2 heading-tight">My Progress</h1>
      <p className="text-sd-mid-gray text-[14.5px] mb-6">
        A short list of the things that actually move your pivot forward. Check them off as they happen.
      </p>

      <ProgressHeader completed={completed} total={total} pct={pct} biggestLever={biggestLever} />

      {/* ---------------- SECTION 1 · QUICK WINS ---------------- */}
      <QuickWinsSection quickWinsDone={qwDone} />

      {/* ---------------- SECTION 2 · BUILD TOWARD IT ---------------- */}
      <BuildTowardItSection
        tasks={buildTowardIt}
        savedJd={savedJd}
        jdDoneSet={jdDoneSet}
        toggleJdAction={toggleJdAction}
        toggleJdActionCompletion={toggleJdActionCompletion}
      />

      {/* Cover letter drafts saved from Role Explorer — untouched from prior design. */}
      <CoverLetterHistory />

      {/* Dark CTA banner (kept per user instruction) */}
      <DarkCtaBanner />
    </div>
  );
}

/* Compute a one-line "biggest lever" nudge from the analysis payload.
   Looks at the lowest skill axis, then falls back to the first Call-1 gap. */
function deriveBiggestLever(analysis) {
  const c1 = analysis?.call1 || {};
  const scores = c1.skill_scores || {};
  let lowest = null;
  for (const [axis, score] of Object.entries(scores)) {
    if (typeof score === 'number' && (lowest === null || score < lowest.score)) {
      lowest = { axis, score };
    }
  }
  if (lowest) {
    return `Your biggest lever is the ${lowest.axis.toLowerCase?.() || lowest.axis} gap (${lowest.score}/10). Start there.`;
  }
  const gaps = c1.gaps || [];
  if (gaps.length && gaps[0]?.category) {
    return `Your biggest lever is closing the ${gaps[0].category} gap. Start there.`;
  }
  return 'Small wins compound. Pick one item and start today.';
}

/* ================================================================================
   PROGRESS HEADER — ring + subtitle
   ================================================================================ */
function ProgressHeader({ completed, total, pct, biggestLever }) {
  // Animate both the ring and the percent counter smoothly from the previous value.
  const [displayPct, setDisplayPct] = useState(pct);
  const [displayCompleted, setDisplayCompleted] = useState(completed);
  const prev = useRef({ pct, completed });

  useEffect(() => {
    const startPct = prev.current.pct;
    const startCompleted = prev.current.completed;
    const t0 = performance.now();
    const duration = 700;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayPct(Math.round(startPct + (pct - startPct) * eased));
      setDisplayCompleted(Math.round(startCompleted + (completed - startCompleted) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else prev.current = { pct, completed };
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pct, completed]);

  const size = 96;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (displayPct / 100) * circ;

  return (
    <div className="sd-card p-6 flex items-center gap-5 flex-wrap" data-testid="progress-header">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E3E5E3" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke="#4CD964" strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 200ms linear' }}
          />
        </svg>
        <div
          className="absolute inset-0 flex items-center justify-center text-sd-black font-semibold"
          style={{ fontSize: '20px', letterSpacing: '-0.02em' }}
          data-testid="progress-percent"
        >
          {displayPct}%
        </div>
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-[13px] uppercase tracking-wider text-sd-mid-gray font-semibold mb-1">Plan progress</div>
        <div className="text-[22px] font-semibold text-sd-black heading-tight" data-testid="progress-fraction">
          {displayCompleted} of {total} steps complete
        </div>
        <div className="text-[13px] text-sd-mid-gray mt-1" data-testid="progress-lever">{biggestLever}</div>
      </div>
    </div>
  );
}

/* ================================================================================
   SECTION 1 · QUICK WINS (auto-tracked, dashed checkboxes)
   ================================================================================ */
function QuickWinsSection({ quickWinsDone }) {
  const done = QUICK_WINS.filter((q) => quickWinsDone.has(q.key)).length;
  return (
    <section className="mt-7 mb-8" data-testid="section-quick-wins">
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className="w-2 h-2 rounded-full bg-sd-primary" />
        <h2 className="text-[15px] font-semibold text-sd-black">Quick Wins</h2>
        <span
          className="text-[10.5px] font-semibold uppercase tracking-[0.09em] px-2 py-0.5 rounded-pill bg-sd-cream text-sd-mid-gray"
          data-testid="quick-wins-badge"
        >
          We tick these for you
        </span>
        <span className="text-[12px] text-sd-mid-gray font-mono ml-auto">· {done} of {QUICK_WINS.length} complete</span>
      </div>
      <p className="text-[13px] text-sd-mid-gray mb-3">Do these inside SD-Pivot — they complete automatically.</p>
      <div className="space-y-2">
        {QUICK_WINS.map((q, i) => (
          <QuickWinCard key={q.key} q={q} idx={i} done={quickWinsDone.has(q.key)} />
        ))}
      </div>
    </section>
  );
}

function QuickWinCard({ q, idx, done }) {
  const { unmarkQuickWin } = useProfileState();
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [wasChecked, setWasChecked] = useState(done);
  const detailRef = useRef(null);
  const checkboxRef = useRef(null);

  useEffect(() => {
    if (detailRef.current) setContentHeight(detailRef.current.scrollHeight);
  }, [expanded]);

  // Toast on the transition from unchecked → checked. Confetti fires
  // centrally inside markQuickWin (fires from any page). This local effect
  // just handles the case where the user is already viewing My Progress
  // when the auto-completion arrives.
  useEffect(() => {
    if (done && !wasChecked) {
      // No "Go to My Progress" action here — the user is ALREADY on My
      // Progress, so the action would be a no-op. Use a plain toast.
      toast.success('Quick Win completed!', { description: q.title, duration: 13500 });
    }
    setWasChecked(done);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  return (
    <div
      className="sd-card overflow-hidden transition-opacity duration-300"
      style={{ opacity: done ? 0.7 : 1 }}
      data-testid={`quick-win-${q.key}`}
    >
      <div className="p-4 flex items-start gap-3">
        {/* DASHED checkbox — signals "system-controlled, you don't tap this". */}
        <div
          ref={checkboxRef}
          aria-label={done ? `${q.title} · completed` : `${q.title} · pending`}
          className={
            'w-6 h-6 mt-0.5 rounded-md shrink-0 flex items-center justify-center text-[14px] font-bold ' +
            (done
              ? 'bg-sd-primary text-white border-2 border-solid border-sd-primary'
              : 'bg-white text-sd-mid-gray border-2 border-dashed border-[#B8C0B8]')
          }
          title={done ? 'Auto-completed' : 'Auto-completes when you do this in the app'}
          data-testid={`quick-win-check-${q.key}`}
        >
          {done ? '✓' : ''}
        </div>

        <div className="flex-1 min-w-0">
          <div
            className={`text-[14px] font-semibold ${done ? 'line-through decoration-2 text-sd-mid-gray' : 'text-sd-black'}`}
          >
            {q.title}
          </div>
          <div className="flex flex-wrap gap-2 mt-2 items-center">
            <span
              className="text-[11px] px-2 py-0.5 rounded-pill font-medium"
              style={done
                ? { background: 'rgba(76, 217, 100, 0.14)', color: '#1F4D2A' }
                : { background: '#F0F2F0', color: '#4A5568' }}
            >
              {done ? q.done_label : q.tag}
            </span>
            <span className="text-[11px] px-2 py-0.5 rounded-pill bg-white border border-dashed border-sd-light-gray text-sd-mid-gray">
              Auto-tracked
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
            if (!expanded) trackEvent(EVENTS.task_expanded, { tag: 'quick_win', type: q.key });
          }}
          className="w-7 h-7 shrink-0 rounded-md border border-sd-light-gray flex items-center justify-center text-sd-mid-gray hover:bg-sd-cream hover:text-sd-black transition-colors"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
          data-testid={`quick-win-expand-${q.key}`}
        >
          <span
            className="text-[16px] font-semibold leading-none inline-block transition-transform duration-300"
            style={{ transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)' }}
          >+</span>
        </button>
      </div>

      <div
        className="overflow-hidden transition-[max-height] duration-300 ease-out"
        style={{ maxHeight: expanded ? `${contentHeight + 20}px` : '0px' }}
        aria-hidden={!expanded}
      >
        <div ref={detailRef} className="px-4 pb-4 pt-1 border-t border-sd-light-gray">
          <div className="mt-3">
            <div className="text-[10.5px] font-semibold tracking-[0.08em] text-sd-mid-gray uppercase mb-1.5">How to do it</div>
            <ol className="text-[13px] text-sd-dark-gray leading-relaxed space-y-1 list-decimal ml-5">
              {q.steps.map((s, i) => <li key={i}>{stripLeadingNumbering(s)}</li>)}
            </ol>
          </div>
          <div className="mt-4 rounded-lg border border-[#E4E9E4] bg-[#F5FAF6] p-3">
            <div className="text-[10.5px] font-semibold tracking-[0.08em] text-[#1F4D2A] uppercase mb-1">Auto-completes when</div>
            <p className="text-[12.5px] text-sd-dark-gray leading-relaxed">{q.how_it_completes}</p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <DeepLinkButton to={q.cta_path} label={q.cta_label} testId={`quick-win-deeplink-${q.key}`} />
            {done && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); unmarkQuickWin(q.key); }}
                className="text-[12px] font-semibold text-sd-mid-gray hover:text-sd-black flex items-center gap-1.5 px-3 py-2 rounded-md border border-sd-light-gray hover:bg-sd-cream transition-colors"
                data-testid={`quick-win-undo-${q.key}`}
                title="Uncheck this Quick Win — I want to do it again"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                </svg>
                Uncheck
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================================
   SECTION 2 · BUILD TOWARD IT (manual, gap-derived + JD-added)
   ================================================================================ */
const TAG_STYLES = {
  skill:       { bg: '#E9FAEC', color: '#1F4D2A', label: 'Skill' },
  network:     { bg: '#ECE6FB', color: '#4B358B', label: 'Network' },
  positioning: { bg: '#FBF1DE', color: '#7A5B16', label: 'Positioning' },
  jd:          { bg: '#E0EEFB', color: '#0A66C2', label: 'From a JD' },
};

function BuildTowardItSection({ tasks, savedJd, jdDoneSet, toggleJdAction, toggleJdActionCompletion }) {
  const totalCount = tasks.length + savedJd.length;
  return (
    <section className="mb-7" data-testid="section-build-toward-it">
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className="w-2 h-2 rounded-full bg-[#7C5BD9]" />
        <h2 className="text-[15px] font-semibold text-sd-black">Build Toward It</h2>
        <span
          className="text-[10.5px] font-semibold uppercase tracking-[0.09em] px-2 py-0.5 rounded-pill bg-sd-cream text-sd-mid-gray"
          data-testid="bti-badge"
        >
          You mark these done
        </span>
        <span className="text-[12px] text-sd-mid-gray font-mono ml-auto">· {totalCount} task{totalCount === 1 ? '' : 's'}</span>
      </div>
      <p className="text-[13px] text-sd-mid-gray mb-3">
        Real-world steps tied to your specific gaps. We can't see these happen — check them off yourself.
      </p>

      {totalCount === 0 && (
        <div className="sd-card p-5 text-center bg-sd-ivory">
          <p className="text-sd-mid-gray text-[13.5px]">No Build Toward It tasks yet. Re-run your assessment to generate a new plan, or add actions from the JD Analyzer.</p>
        </div>
      )}

      <div className="space-y-2">
        {tasks.map((t, i) => (
          <BuildTowardItCard key={`bti_${i}`} t={t} idx={i} />
        ))}
        {savedJd.map((key, i) => (
          <JdActionCard
            key={key}
            actionKey={key}
            idx={i}
            done={jdDoneSet.has(key)}
            onToggleDone={() => toggleJdActionCompletion(key)}
            onRemove={() => toggleJdAction(key)}
          />
        ))}
      </div>
    </section>
  );
}

function BuildTowardItCard({ t, idx }) {
  const { progress, toggleMilestone } = useProfileState();
  const key = `bti_${t.title}`;
  const done = (progress?.completed_milestones || []).includes(key);
  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [wasChecked, setWasChecked] = useState(done);
  const detailRef = useRef(null);
  const checkboxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { if (detailRef.current) setContentHeight(detailRef.current.scrollHeight); }, [expanded, t]);

  useEffect(() => {
    if (done && !wasChecked) {
      const el = checkboxRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        fireConfetti(rect.left + rect.width / 2, rect.top + rect.height / 2);
      }
      toast.success('Task completed!', { description: t.title, duration: 3000 });
      trackEvent(EVENTS.task_marked_done, { tag: t.tag || 'skill', type: t.type || 'learn-first' });
    }
    setWasChecked(done);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const style = TAG_STYLES[(t.tag || 'skill').toLowerCase()] || TAG_STYLES.skill;

  return (
    <div
      className="sd-card overflow-hidden transition-opacity duration-300"
      style={{ opacity: done ? 0.55 : 1 }}
      data-testid={`bti-card-${idx}`}
    >
      <div className="p-4 flex items-start gap-3">
        <button
          ref={checkboxRef}
          onClick={(e) => { e.stopPropagation(); toggleMilestone(key); }}
          aria-label={done ? 'Mark as incomplete' : 'Mark as complete'}
          className={`w-6 h-6 mt-0.5 rounded-md shrink-0 flex items-center justify-center text-white text-[14px] font-bold transition-colors ${done ? 'bg-sd-primary' : 'bg-sd-light-gray text-sd-mid-gray hover:bg-[#D5D8D5]'}`}
          data-testid={`bti-check-${idx}`}
        >
          {done ? '✓' : ''}
        </button>

        <div className="flex-1 min-w-0">
          <div className={`text-[14px] font-semibold ${done ? 'line-through decoration-2 text-sd-mid-gray' : 'text-sd-black'}`}>
            {t.title}
          </div>
          <div className="flex flex-wrap gap-2 mt-2 items-center">
            <span
              className="text-[11px] px-2 py-0.5 rounded-pill font-medium"
              style={{ background: style.bg, color: style.color }}
              data-testid={`bti-tag-${idx}`}
            >
              {style.label}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
            if (!expanded) trackEvent(EVENTS.task_expanded, { tag: t.tag || 'skill', type: t.type || 'learn-first' });
          }}
          className="w-7 h-7 shrink-0 rounded-md border border-sd-light-gray flex items-center justify-center text-sd-mid-gray hover:bg-sd-cream hover:text-sd-black transition-colors"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
          data-testid={`bti-expand-${idx}`}
        >
          <span
            className="text-[16px] font-semibold leading-none inline-block transition-transform duration-300"
            style={{ transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)' }}
          >+</span>
        </button>
      </div>

      <div
        className="overflow-hidden transition-[max-height] duration-300 ease-out"
        style={{ maxHeight: expanded ? `${contentHeight + 20}px` : '0px' }}
        aria-hidden={!expanded}
      >
        <div ref={detailRef} className="px-4 pb-4 pt-1 border-t border-sd-light-gray space-y-3">
          {Array.isArray(t.steps) && t.steps.length > 0 && (
            <div>
              <div className="text-[10.5px] font-semibold tracking-[0.08em] text-sd-mid-gray uppercase mb-1.5">Steps</div>
              <ol className="text-[13px] text-sd-dark-gray leading-relaxed space-y-1 list-decimal ml-5">
                {t.steps.map((s, i) => {
                  const clean = stripLeadingNumbering(s);
                  const isLast = i === t.steps.length - 1;
                  return (
                    <li key={i} data-testid={isLast ? `bti-step-last-${idx}` : undefined}>
                      {isLast ? <BoldDeliverable text={clean} /> : clean}
                    </li>
                  );
                })}
              </ol>
              {/* Add-to-CV/LinkedIn blurb — Fix 2: turn the last step's deliverable
                  into a resume/LinkedIn bullet. Static instruction keeps the flow
                  actionable without needing a fresh LLM re-run for old analyses. */}
              <div
                className="mt-2.5 text-[12px] text-sd-mid-gray leading-relaxed italic"
                data-testid={`bti-cv-blurb-${idx}`}
              >
                <span className="not-italic font-semibold text-sd-dark-gray">Add it to your CV / LinkedIn:</span>{' '}
                paste the bolded outcome above as a bullet under your current role
                (start with a verb, keep the metric).
              </div>
            </div>
          )}
          {t.why && (
            <div>
              <div className="text-[10.5px] font-semibold tracking-[0.08em] text-sd-mid-gray uppercase mb-1">Why now</div>
              <p className="text-[13px] text-sd-dark-gray leading-relaxed">{t.why}</p>
            </div>
          )}
          {t.gap_line && (
            <div className="rounded-lg border-l-4 p-3 bg-sd-ivory" style={{ borderColor: style.color }}>
              <div className="text-[10.5px] font-semibold tracking-[0.08em] uppercase mb-1" style={{ color: style.color }}>The gap this closes</div>
              <p className="text-[13px] text-sd-dark-gray leading-relaxed">{t.gap_line}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-center pt-1">
            {t.resource_url && (
              <a
                href={t.resource_url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="sd-btn-primary inline-flex items-center gap-2 !py-2 !px-3.5 !text-[12.5px]"
                data-testid={`bti-resource-${idx}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
                {t.resource_label || 'Open resource'}
              </a>
            )}
            {t.tool_tip && (
              <div className="text-[12px] text-sd-mid-gray italic flex-1 min-w-[220px]">
                <span className="font-semibold text-sd-dark-gray">Tip:</span> {t.tool_tip}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* JD-added task card — preserves the "add to My Progress" flow from the JD
   Analyzer. Same visual treatment as a Build Toward It card, but with a JD
   tag + remove button (users can prune tasks they no longer want). */
function JdActionCard({ actionKey, idx, done, onToggleDone, onRemove }) {
  const label = actionKey.replace(/^jd_/, '');
  const style = TAG_STYLES.jd;
  const checkboxRef = useRef(null);
  const [wasChecked, setWasChecked] = useState(done);

  useEffect(() => {
    if (done && !wasChecked) {
      const el = checkboxRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        fireConfetti(rect.left + rect.width / 2, rect.top + rect.height / 2);
      }
      toast.success('Task completed!', { description: label, duration: 3000 });
      trackEvent(EVENTS.task_marked_done, { tag: 'jd' });
    }
    setWasChecked(done);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  return (
    <div
      className="sd-card p-4 flex items-start gap-3 transition-opacity duration-300"
      style={{ opacity: done ? 0.55 : 1 }}
      data-testid={`jd-action-card-${idx}`}
    >
      <button
        ref={checkboxRef}
        onClick={onToggleDone}
        aria-pressed={done}
        title={done ? 'Mark as not done' : 'Mark as done'}
        className={`w-6 h-6 mt-0.5 rounded-md shrink-0 flex items-center justify-center text-[14px] font-bold transition-colors ${done ? 'bg-sd-primary text-white' : 'bg-sd-light-gray text-sd-mid-gray hover:bg-sd-cream border border-sd-hairline'}`}
        data-testid={`jd-action-check-${idx}`}
      >
        {done ? '✓' : ''}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-[14px] leading-snug ${done ? 'text-sd-mid-gray line-through' : 'text-sd-dark-gray'}`}>
          {label}
        </div>
        <div className="mt-2">
          <span
            className="text-[11px] px-2 py-0.5 rounded-pill font-medium"
            style={{ background: style.bg, color: style.color }}
            data-testid={`jd-action-tag-${idx}`}
          >
            {style.label}
          </span>
        </div>
      </div>
      <button
        onClick={onRemove}
        className="text-sd-mid-gray hover:text-sd-error text-[12px] font-medium shrink-0"
        title="Remove from plan"
        data-testid={`jd-action-remove-${idx}`}
      >
        Remove
      </button>
    </div>
  );
}

/* ================================================================================
   DARK CTA BANNER — kept from prior design so the CTA to run more JDs is intact.
   ================================================================================ */
function DarkCtaBanner() {
  const navigate = useNavigate();
  const go = () => { trackDeeplink('/dashboard/jd'); navigate('/dashboard/jd'); };
  return (
    <section
      className="rounded-card p-8 mt-7 text-white cursor-pointer transition-all hover:translate-y-[-2px]"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #1C2A1C 100%)' }}
      onClick={go}
      data-testid="progress-cta-banner"
    >
      <div className="flex items-center justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <h2 className="text-[22px] font-semibold heading-tight mb-2">Ready to test your readiness?</h2>
          <p className="text-[#B8C0B8] text-[14.5px] leading-relaxed max-w-[560px]">
            Paste a real job description and see how these tasks map to a specific posting. Save more targeted actions to your plan.
          </p>
        </div>
        <button
          className="sd-btn-primary inline-flex items-center gap-2 !py-3 !px-5 !text-[14px] shrink-0"
          onClick={(e) => { e.stopPropagation(); go(); }}
          data-testid="progress-cta-button"
        >
          Open JD Analyzer
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
          </svg>
        </button>
      </div>
    </section>
  );
}




/* ================================================================================
   COVER LETTER HISTORY
   Saved drafts (from Role Explorer's cover-letter modal) grouped by role,
   with per-entry actions: View, Copy, Download, Delete, plus Reload-into-editor
   to keep iterating.
   ================================================================================ */
function CoverLetterHistory() {
  const { savedProfile } = useProfileState();
  const { user } = useAuth();
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLetter, setActiveLetter] = useState(null); // reload target
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [compareOpen, setCompareOpen] = useState(false);

  // The candidate block enrichs the saved-letter payload before it's rendered
  // as a PDF or emailed, so the exported document opens with a proper cover
  // sheet (name / title / email · location) even for older drafts.
  const candidate = useMemo(() => ({
    name: savedProfile?.name || user?.name || '',
    title: savedProfile?.title || '',
    email: user?.email || '',
    location: savedProfile?.location || '',
    linkedin: savedProfile?.linkedin_url || '',
  }), [savedProfile, user]);
  const withCandidate = React.useCallback((l) => ({ ...l, candidate }), [candidate]);

  const refresh = React.useCallback(async () => {
    try {
      const resp = await analysisApi.listCoverLetters();
      setLetters(resp.letters || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const l of letters) {
      const key = (l.role_title || 'Untitled').trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    // Preserve insertion order (already newest-first from backend)
    return Array.from(map.entries()).map(([role_title, items]) => ({ role_title, items }));
  }, [letters]);

  const remove = async (id) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await analysisApi.deleteCoverLetter(id);
      setLetters((prev) => prev.filter((l) => l.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success('Draft removed');
    } catch {
      toast.error('Could not remove that draft.');
    }
  };

  // Compare selection helpers — cap at 2 (side-by-side comparison).
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= 2) {
          // Bump the oldest selection out so the newest tap always sticks —
          // feels more forgiving than a hard "you can only pick two" error.
          const [oldest] = next;
          next.delete(oldest);
        }
        next.add(id);
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectedLetters = useMemo(
    () => letters.filter((l) => selectedIds.has(l.id)),
    [letters, selectedIds]
  );

  const renderHeader = (label = null) => (
    <div className="flex items-center gap-2 mb-3">
      <span className="w-2 h-2 rounded-full" style={{ background: '#4CD964' }} />
      <h2 className="text-[15px] font-semibold text-sd-black">Cover letter drafts</h2>
      {label && <span className="text-[12px] text-sd-mid-gray font-mono">· {label}</span>}
    </div>
  );

  if (loading) {
    return (
      <section className="mb-7" data-testid="cover-letter-history-loading">
        {renderHeader('loading…')}
        <div className="sd-card p-4 text-[13.5px] text-sd-mid-gray">Loading your drafts…</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mb-7">
        {renderHeader()}
        <div className="sd-card p-4 text-[13.5px] text-sd-error" data-testid="cover-letter-history-error">{error}</div>
      </section>
    );
  }

  if (letters.length === 0) {
    return (
      <section className="mb-7" data-testid="cover-letter-history-empty">
        {renderHeader('none saved yet')}
        <div className="sd-card p-5">
          <div className="text-[14px] text-sd-dark-gray leading-relaxed">
            No drafts saved yet. Head to <b>Role Explorer</b>, expand any role, and use <b>Draft cover letter</b>. When you copy or download a draft, it lands here.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-7" data-testid="cover-letter-history">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2 h-2 rounded-full" style={{ background: '#4CD964' }} />
        <h2 className="text-[15px] font-semibold text-sd-black">Cover letter drafts</h2>
        <span className="text-[12px] text-sd-mid-gray font-mono">· {letters.length} draft{letters.length === 1 ? '' : 's'} saved</span>
      </div>
      <div className="space-y-4">
        {grouped.map(({ role_title, items }) => (
          <RoleGroup
            key={role_title}
            roleTitle={role_title}
            items={items}
            candidate={candidate}
            onReload={(letter) => setActiveLetter(letter)}
            onDelete={remove}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
          />
        ))}
      </div>

      {/* Floating action bar — visible only while a comparison selection exists */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 sd-card !bg-sd-black !border-sd-black flex items-center gap-3 py-2.5 pl-4 pr-2.5 shadow-card-deep animate-fade-in" data-testid="cover-letter-compare-bar" role="region" aria-label="Draft comparison bar">
          <div className="text-[13px] text-white font-medium">
            {selectedIds.size} of 2 draft{selectedIds.size === 1 ? '' : 's'} selected
          </div>
          <button
            type="button"
            onClick={clearSelection}
            className="text-[12.5px] text-white/70 hover:text-white transition-colors px-2"
            data-testid="cover-letter-compare-clear"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            disabled={selectedIds.size < 2}
            className="sd-btn-primary !py-2 !px-3.5 !text-[13px] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cover-letter-compare-open"
          >
            Compare side-by-side
          </button>
        </div>
      )}

      {compareOpen && selectedLetters.length === 2 && (
        <CoverLetterCompareModal
          left={selectedLetters[0]}
          right={selectedLetters[1]}
          candidate={candidate}
          onClose={() => setCompareOpen(false)}
        />
      )}

      {activeLetter && (
        <CoverLetterModal
          role={{
            title: activeLetter.role_title,
            sector: activeLetter.role_sector,
            why: activeLetter.role_snapshot?.why,
            skills_present: activeLetter.role_snapshot?.skills_present || [],
            skills_gap: activeLetter.role_snapshot?.skills_gap || [],
            key_gap: activeLetter.role_snapshot?.key_gap,
          }}
          initialDraft={activeLetter}
          onClose={() => { setActiveLetter(null); refresh(); }}
          onSaved={(letter) => {
            // The modal saves a new snapshot; prepend it so the user sees it
            // immediately when they close.
            setLetters((prev) => [letter, ...prev.filter((l) => l.id !== letter.id)]);
          }}
        />
      )}
    </section>
  );
}


function RoleGroup({ roleTitle, items, candidate, onReload, onDelete, selectedIds, onToggleSelect }) {
  const [expanded, setExpanded] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);

  // Merge candidate details into the letter object right before we hand it
  // off to the PDF helper. Keeps the row action code identical regardless of
  // where the candidate info came from.
  const withCandidate = (l) => ({ ...l, candidate });

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const copy = async (letter) => {
    const full = `${letter.opener || 'Dear Hiring Manager,'}\n\n${letter.body}\n\n${letter.closer || 'With appreciation,'}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopiedId(letter.id);
      setTimeout(() => setCopiedId(null), 1600);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — try opening the draft.');
    }
  };

  const download = (letter) => {
    const full = `${letter.opener || 'Dear Hiring Manager,'}\n\n${letter.body}\n\n${letter.closer || 'With appreciation,'}`;
    const blob = new Blob([full], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = (letter.role_title || 'role').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
    a.download = `cover-letter-${safe}-${letter.tone}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 400);
  };

  const downloadPdf = (letter) => {
    const filename = downloadCoverLetterPdf(withCandidate(letter));
    toast.success(`Downloaded ${filename}`);
  };

  // See CoverLetterModal.email — mailto can't carry attachments, so we auto-
  // download the PDF and prime the mail draft with an attach reminder.
  const email = (letter) => {
    const filename = emailCoverLetterAsPdf(withCandidate(letter));
    toast.info(`PDF downloaded — attach ${filename} to the email that just opened`);
  };

  return (
    <div className="sd-card overflow-hidden" data-testid={`cover-letter-group-${(roleTitle || '').toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="px-5 py-3 border-b border-sd-light-gray flex items-center justify-between gap-3 bg-sd-ivory">
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-sd-black truncate">{roleTitle}</div>
          <div className="text-[11.5px] text-sd-mid-gray">
            {items.length} of 5 draft{items.length === 1 ? '' : 's'}
            {items.length >= 5 && (
              <span className="ml-2 text-sd-mid-gray/80 italic">· oldest is auto-pruned when you save a 6th</span>
            )}
          </div>
        </div>
      </div>
      <ul className="divide-y divide-sd-light-gray">
        {items.map((l) => {
          const isOpen = expanded.has(l.id);
          const isCopied = copiedId === l.id;
          return (
            <li key={l.id} className="px-5 py-4" data-testid={`cover-letter-entry-${l.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer select-none" title="Select to compare (max 2)">
                  <input
                    type="checkbox"
                    checked={selectedIds?.has(l.id) || false}
                    onChange={() => onToggleSelect?.(l.id)}
                    className="w-4 h-4 rounded border-sd-mid-gray accent-sd-primary cursor-pointer"
                    data-testid={`cover-letter-select-${l.id}`}
                    aria-label={`Select this ${l.tone} draft for comparison`}
                  />
                </label>
                <ToneBadge tone={l.tone} />
                <div className="text-[12.5px] text-sd-mid-gray tabular-nums">{relativeTime(l.created_at)}</div>
                <div className="text-[12px] text-sd-mid-gray">· {l.word_count} words</div>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => toggle(l.id)}
                  className="text-[12.5px] font-medium text-sd-mid-gray hover:text-sd-black transition-colors"
                  aria-expanded={isOpen}
                  data-testid={`cover-letter-view-${l.id}`}
                >
                  {isOpen ? 'Hide' : 'View'}
                </button>
                <button
                  type="button"
                  onClick={() => copy(l)}
                  className="text-[12.5px] font-medium text-sd-mid-gray hover:text-sd-black transition-colors"
                  data-testid={`cover-letter-copy-${l.id}`}
                >
                  {isCopied ? 'Copied' : 'Copy'}
                </button>
                <button
                  type="button"
                  onClick={() => downloadPdf(l)}
                  className="text-[12.5px] font-medium text-sd-mid-gray hover:text-sd-black transition-colors"
                  data-testid={`cover-letter-pdf-${l.id}`}
                  title="Download as PDF"
                >
                  PDF
                </button>
                <button
                  type="button"
                  onClick={() => download(l)}
                  className="text-[12.5px] font-medium text-sd-mid-gray hover:text-sd-black transition-colors"
                  data-testid={`cover-letter-download-${l.id}`}
                  title="Download plain text"
                >
                  .txt
                </button>
                <button
                  type="button"
                  onClick={() => email(l)}
                  className="text-[12.5px] font-medium text-sd-mid-gray hover:text-sd-black transition-colors"
                  data-testid={`cover-letter-email-${l.id}`}
                  title="Downloads a PDF + opens your email client with an attach reminder"
                >
                  Email as PDF
                </button>
                <button
                  type="button"
                  onClick={() => onReload(l)}
                  className="text-[12.5px] font-medium text-sd-primary-dark hover:text-sd-primary transition-colors"
                  data-testid={`cover-letter-reload-${l.id}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(l.id)}
                  className="text-[12.5px] font-medium text-sd-mid-gray hover:text-sd-error transition-colors"
                  data-testid={`cover-letter-delete-${l.id}`}
                  aria-label="Delete draft"
                >
                  Delete
                </button>
              </div>

              {isOpen && (
                <div className="mt-3 rounded-lg bg-sd-cream/60 p-4 text-[13.5px] leading-[1.65] text-sd-dark-gray whitespace-pre-wrap" data-testid={`cover-letter-preview-${l.id}`}>
                  <p className="text-sd-black font-medium mb-2">{l.opener}</p>
                  <p className="mb-2">{l.body}</p>
                  <p className="text-sd-black font-medium">{l.closer}</p>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function ToneBadge({ tone }) {
  const map = {
    formal: { label: 'Formal', bg: '#EEF5FC', fg: '#0A66C2' },
    warm:   { label: 'Warm',   bg: '#FFF1E0', fg: '#B26A1A' },
    bold:   { label: 'Bold',   bg: '#F0EBFA', fg: '#5A3EBF' },
    custom: { label: 'Custom', bg: '#F0F0F0', fg: '#2F2F2F' },
  };
  const t = map[tone] || map.formal;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]"
      style={{ background: t.bg, color: t.fg }}
    >
      {t.label}
    </span>
  );
}


function relativeTime(ts) {
  if (!ts) return '';
  const seconds = Math.floor(Date.now() / 1000 - ts);
  if (seconds < 60) return 'Just now';
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}


/* ================================================================================
   CoverLetterCompareModal
   Side-by-side view of two saved drafts. Read-only — the user comes here to
   choose which one to send. Includes a per-column Copy action so they can
   commit their pick in one click.
   ================================================================================ */
function CoverLetterCompareModal({ left, right, candidate, onClose }) {
  const [copiedSide, setCopiedSide] = useState(null);

  const copyFull = async (side, letter) => {
    const full = `${letter.opener || 'Dear Hiring Manager,'}\n\n${letter.body}\n\n${letter.closer || 'With appreciation,'}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopiedSide(side);
      setTimeout(() => setCopiedSide(null), 1600);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — open the draft instead.');
    }
  };

  const emailLetter = (letter) => {
    const filename = emailCoverLetterAsPdf({ ...letter, candidate });
    toast.info(`PDF downloaded — attach ${filename} to the email that just opened`);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center px-4 py-6 animate-fade-in"
      onClick={onClose}
      data-testid="cover-letter-compare-modal"
    >
      <div
        className="bg-white rounded-card shadow-card-deep max-w-[1180px] w-full max-h-[92vh] overflow-hidden flex flex-col animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-sd-light-gray flex items-center justify-between gap-4 shrink-0">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-sd-mid-gray font-semibold">Compare drafts</div>
            <div className="text-[16px] font-semibold text-sd-black mt-0.5">Pick the one that lands best</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-sd-cream flex items-center justify-center text-sd-mid-gray hover:text-sd-black transition-colors"
            aria-label="Close comparison"
            data-testid="cover-letter-compare-close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Two-column body */}
        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-sd-light-gray">
          <CompareColumn side="left" letter={left} copied={copiedSide === 'left'} onCopy={() => copyFull('left', left)} onEmail={() => emailLetter(left)} />
          <CompareColumn side="right" letter={right} copied={copiedSide === 'right'} onCopy={() => copyFull('right', right)} onEmail={() => emailLetter(right)} />
        </div>

        {/* Footer legend */}
        <div className="px-6 py-3 border-t border-sd-light-gray bg-sd-ivory text-[12px] text-sd-mid-gray flex items-center justify-between gap-3 shrink-0">
          <span>Read-only view. Use <b>Edit</b> in the history list to keep tweaking a draft.</span>
          <span className="tabular-nums">{left.word_count} vs {right.word_count} words</span>
        </div>
      </div>
    </div>
  );
}


function CompareColumn({ side, letter, copied, onCopy, onEmail }) {
  return (
    <div className="p-6 overflow-y-auto" data-testid={`compare-column-${side}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <ToneBadge tone={letter.tone} />
            <div className="text-[11.5px] text-sd-mid-gray tabular-nums">{relativeTime(letter.created_at)}</div>
            <div className="text-[11.5px] text-sd-mid-gray">· {letter.word_count} words</div>
          </div>
          <div className="text-[14.5px] font-semibold text-sd-black mt-1.5 truncate">{letter.role_title}</div>
          {letter.tone === 'custom' && letter.tone_hint && (
            <div className="text-[11.5px] text-sd-mid-gray italic mt-0.5 truncate" title={letter.tone_hint}>
              "{letter.tone_hint}"
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onEmail}
            className="sd-btn-secondary !py-1.5 !px-2.5 !text-[12px] inline-flex items-center gap-1"
            data-testid={`compare-email-${side}`}
            title="Downloads a PDF + opens your email client with an attach reminder"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Email as PDF
          </button>
          <button
            type="button"
            onClick={onCopy}
            className="sd-btn-secondary !py-1.5 !px-2.5 !text-[12px] inline-flex items-center gap-1"
            data-testid={`compare-copy-${side}`}
          >
            {copied ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4CD964" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Copied
              </>
            ) : 'Copy'}
          </button>
        </div>
      </div>

      <article className="text-[13.5px] leading-[1.7] text-sd-dark-gray whitespace-pre-wrap font-sans" data-testid={`compare-body-${side}`}>
        <p className="text-sd-black font-medium mb-3">{letter.opener || 'Dear Hiring Manager,'}</p>
        {(letter.body || '').split(/\n{2,}/).map((para, i) => (
          <p key={i} className="mb-3">{para.trim()}</p>
        ))}
        <p className="text-sd-black font-medium mt-2">{letter.closer || 'With appreciation,'}</p>
      </article>
    </div>
  );
}
